import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';

import { TeacherDocumentDownloadUrlQuery } from 'core/operations/queries/teacherDocuments';

import {
  ErpTeacherDocumentsQuery,
  Maybe,
  useErpTeacherDocumentsQuery,
  useErpTeacherDocumentTextQuery,
  useRemoveTeacherDocumentMutation,
  useRunDocumentAnalysisMutation,
  useSaveDocumentTextMutation,
  useUploadTeacherDocumentMutation,
} from 'core/graphql';

export type ITeacherDocument = ErpTeacherDocumentsQuery['teacherDocuments'][number];

/** Lowercase strings on the wire (`teachers.interview_constants`). */
export const DOCUMENT_KINDS = [
  'cv',
  'certificate',
  'portfolio',
  'other',
] as const;
export type DocumentKind = typeof DOCUMENT_KINDS[number];

export const EXTRACTION_STATUSES = [
  'pending',
  'done',
  'failed',
  'unsupported',
] as const;
export type ExtractionStatus = typeof EXTRACTION_STATUSES[number];

export const EXTRACTION_STATUS_COLOR: Record<ExtractionStatus, string> = {
  pending: 'default',
  done: 'green',
  // Amber, not red: `failed` on a valid PDF means «это скан, текстового слоя
  // нет» — the file is fine and there is a manual-paste recovery.
  failed: 'orange',
  unsupported: 'default',
};

/** Only a CV is restricted to PDF — it is the one whose text feeds the model. */
export const CV_ACCEPT = 'application/pdf,.pdf';

const DOCUMENT_ERROR_CODES = [
  'NOT_FOUND',
  'INVALID_INPUT',
  'UNSUPPORTED_FILE',
  'AI_DISABLED',
  'NO_DOCUMENT_TEXT',
] as const;

interface IDocumentCustomError {
  code?: Maybe<string>;
  message?: Maybe<string>;
}

export const documentErrorMessage = (
  t: (key: string) => string,
  customErrors?: Maybe<Array<Maybe<IDocumentCustomError>>>,
): string => {
  const first = (customErrors || []).find(Boolean);
  const code = first?.code || '';
  if ((DOCUMENT_ERROR_CODES as readonly string[]).includes(code)) {
    return t(`pages.teacherDetail.documents.errors.${code}`);
  }
  return first?.message || t('pages.teacherDetail.documents.errors.generic');
};

interface IActionResult {
  success: boolean;
  errorMessage?: string;
}

/**
 * Spec 0028 D — the candidate's documents.
 *
 * Uploads are VERSIONED server-side, so the list always refetches rather than
 * patching the cache: a new upload does not replace a row, it adds one and moves
 * `isCurrent`.
 *
 * A CV analysis also changes the AI profile, so those mutations refetch
 * `ErpTeacherAiProfile` too — the merged profile is what the operator is
 * actually watching.
 */
export const useTeacherDocuments = (teacherId?: string) => {
  const { t } = useTranslation();
  const { data, loading, refetch } = useErpTeacherDocumentsQuery({
    // `teacherId || ''` rather than a cast: an empty required ID still satisfies
    // the contract, whereas `undefined` goes out as no variable and 400s — which
    // any refetch-by-name of this query would then trip over.
    // No `kind` filter: the panel shows every kind, and the server-side argument
    // stays available for a future per-kind view.
    variables: { teacherId: teacherId || '' },
    skip: !teacherId,
    fetchPolicy: 'cache-and-network',
  });

  const refetchList = { refetchQueries: ['ErpTeacherDocuments'] };
  const [uploadRun] = useUploadTeacherDocumentMutation(refetchList);
  // ⚠️ `ErpTeacherDocumentText` is deliberately NOT refetched by name — same
  // trap as `ErpTeacherInterviewTranscript` (see useTeacherInterviews):
  // refetch-by-name also re-runs the `skip`ped instance the drawer leaves
  // behind, which goes out with no variables, 400s on `$documentId: ID!` and
  // sends errorLink to /500 after a mutation that SUCCEEDED. The drawer
  // refetches its own query, bound to real variables.
  const [saveTextRun] = useSaveDocumentTextMutation(refetchList);
  const [removeRun] = useRemoveTeacherDocumentMutation({
    // Deleting a CV nulls `document` on its run row, which the AI panel shows.
    refetchQueries: ['ErpTeacherDocuments', 'ErpTeacherAiProfile'],
  });
  const [analyseRun] = useRunDocumentAnalysisMutation({
    refetchQueries: ['ErpTeacherAiProfile'],
  });
  const [pending, setPending] = useState(false);
  const client = useApolloClient();

  /** Fetched ON CLICK: the URL is short-lived and every issue writes an audit
   *  row, so caching it in a list would both rot and lie about who looked. */
  const getDownloadUrl = useCallback(
    async (documentId: string): Promise<string | undefined> => {
      const result = await client.query({
        query: TeacherDocumentDownloadUrlQuery,
        variables: { documentId },
        fetchPolicy: 'network-only',
      });
      return result.data?.teacherDocumentDownloadUrl || undefined;
    },
    [client],
  );

  const run = useCallback(
    async (
      call: () => Promise<
        | {
            success?: boolean | null;
            customErrors?: Maybe<Array<Maybe<IDocumentCustomError>>>;
          }
        | null
        | undefined
      >,
    ): Promise<IActionResult> => {
      setPending(true);
      try {
        const payload = await call();
        if (payload?.success) return { success: true };
        return {
          success: false,
          errorMessage: documentErrorMessage(t, payload?.customErrors),
        };
      } catch (e) {
        // PermissionDenied lands in GraphQL `errors`, so the promise REJECTS.
        return { success: false, errorMessage: (e as Error).message };
      } finally {
        setPending(false);
      }
    },
    [t],
  );

  const upload = useCallback(
    (file: File, documentKind: DocumentKind) =>
      run(async () => {
        // The raw File goes straight into variables — apollo-upload-client's
        // createUploadLink switches to multipart when it sees one. A multipart
        // fetch reports no byte progress, hence the indeterminate spinner.
        const result = await uploadRun({
          variables: {
            teacherId: teacherId as string,
            file,
            kind: documentKind,
          },
        });
        return result.data?.uploadTeacherDocument;
      }),
    [run, uploadRun, teacherId],
  );

  const saveText = useCallback(
    (documentId: string, text: string) =>
      run(async () => {
        const result = await saveTextRun({ variables: { documentId, text } });
        return result.data?.setTeacherDocumentText;
      }),
    [run, saveTextRun],
  );

  const remove = useCallback(
    (documentId: string) =>
      run(async () => {
        const result = await removeRun({ variables: { documentId } });
        return result.data?.deleteTeacherDocument;
      }),
    [run, removeRun],
  );

  const analyse = useCallback(
    (documentId: string) =>
      run(async () => {
        const result = await analyseRun({ variables: { documentId } });
        return result.data?.runTeacherDocumentAnalysis;
      }),
    [run, analyseRun],
  );

  return {
    documents: data?.teacherDocuments || [],
    loading,
    pending,
    refetch,
    upload,
    saveText,
    remove,
    analyse,
    getDownloadUrl,
  };
};

/**
 * The extracted body, loaded only when the drawer opens.
 *
 * `refetch` is returned so the drawer reloads ITS OWN query after a save — see
 * the comment on `saveTextRun` for why the mutation must not refetch this one
 * by name.
 */
export const useTeacherDocumentText = (documentId?: string) => {
  const { data, loading, refetch } = useErpTeacherDocumentTextQuery({
    variables: { documentId: documentId as string },
    skip: !documentId,
    fetchPolicy: 'cache-and-network',
  });
  return {
    document: data?.teacherDocumentText || undefined,
    loading,
    refetch,
  };
};
